/**
 * AiTerminalContextBar
 *
 * Displays connection and database context for an AI terminal session.
 * Shows engine icon, server name, database, and workspace status.
 * Expandable to show additional context details.
 * Shows upsell message for Personal mode users.
 */

import { useState } from 'react';
import { ChevronDown, ChevronUp, Database, ExternalLink, Server, Sparkles } from 'lucide-react';
import { useConnectionStore } from '../../connections/store';
import { useHasProAccess, useProUpgradePromptStore } from '../../../core/licensing';
import type { TerminalSession } from '../../../types/terminal';
import { AgentStartButtons } from './AgentStartButtons';
import { AgentActivityChip } from './AgentActivityChip';
import { McpQuickEnableChip } from './McpQuickEnableChip';
import { AddSkillNudgeRow } from './AddSkillNudgeRow';
import { PersistentWorkspaceToggleRow } from './PersistentWorkspaceToggleRow';
import { safeConnectionName, safeConnectionServer } from '../../../types/safeConnectionDisplay';

type AiTerminalContextBarProps = {
  session: TerminalSession;
};

/**
 * Get engine icon based on engine type
 */
function getEngineIcon(engine: string = 'mssql'): React.ReactNode {
  switch (engine) {
    case 'pgsql':
      return <span className="text-base">🐘</span>;
    case 'sqlite':
      return <span className="text-base">📦</span>;
    case 'mssql':
    default:
      return <span className="text-base">🔷</span>;
  }
}

/**
 * Get engine display name
 */
function getEngineName(engine: string = 'mssql'): string {
  switch (engine) {
    case 'pgsql':
      return 'PostgreSQL';
    case 'sqlite':
      return 'SQLite';
    case 'mssql':
    default:
      return 'SQL Server';
  }
}

export function AiTerminalContextBar({ session }: AiTerminalContextBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasProAccess = useHasProAccess();
  const showUpgradePrompt = useProUpgradePromptStore((s) => s.showPrompt);

  // Handler to open upgrade dialog
  const handleUpgradeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    showUpgradePrompt('ai-workspace');
  };

  // Get connection info
  const activeConnections = useConnectionStore((s) => s.activeConnections);
  const connection = activeConnections.find((c) => c.id === session.connectionId);
  const connInfo = connection?.connection;

  // Not an AI terminal or no connection
  if (session.type !== 'ai' || !connInfo) {
    return null;
  }

  const engine = connInfo.engine || 'mssql';
  const serverName = safeConnectionName(connInfo);
  const databaseName = session.database || connInfo.database || 'Default';
  const isConnected = connection?.status === 'connected';
  // 'complete', 'error', and 'cancelled' are all terminal init phases — none
  // of them should keep the bar showing the amber "Initializing..." state.
  // A cancelled export leaves the terminal usable, so it falls back to the
  // same resting status as a session with no init state at all (Workspace
  // Ready / Basic Shell below), matching the "AI terminal stays available"
  // message shown when the user cancelled.
  const isInitializing =
    !!session.initState &&
    session.initState.phase !== 'complete' &&
    session.initState.phase !== 'error' &&
    session.initState.phase !== 'cancelled';
  const initError = session.initState?.phase === 'error';

  // Workspace status text - different for Pro vs Personal
  const workspaceStatus = initError
    ? 'Error'
    : isInitializing
      ? session.initState?.message || 'Initializing...'
      : hasProAccess
        ? 'Workspace Ready'
        : 'Basic Shell';

  const statusColor = initError
    ? 'text-red-500'
    : isInitializing
      ? 'text-warning'
      : hasProAccess
        ? 'text-green-500'
        : 'text-muted-foreground';

  const handleOpenWorkspace = async () => {
    if (session.workspacePath && window.electronAPI?.showItemInFolder) {
      const result = await window.electronAPI.showItemInFolder(session.workspacePath);
      if (!result.success) {
        console.error('[AiTerminalContextBar] Failed to open workspace:', result.error);
      }
    }
  };

  return (
    <div className="border-b border-border bg-muted/30 select-none">
      {/* Collapsed state - main bar */}
      <button
        className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-muted/50 transition-colors text-left"
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid="ai-context-bar"
      >
        <div className="flex items-center gap-2 text-sm">
          {/* Engine icon */}
          {getEngineIcon(engine)}

          {/* Server and database */}
          <span className="font-medium select-text">{serverName}</span>
          <span className="text-muted-foreground">│</span>
          <Database className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground select-text">{databaseName}</span>
        </div>

        <div className="flex items-center gap-3 text-xs">
          {/* Connection status */}
          <span className={isConnected ? 'text-green-500' : 'text-red-500'}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
          <span className="text-muted-foreground">•</span>
          {/* Workspace status */}
          <span className={statusColor}>
            {workspaceStatus}
          </span>

          {/* Agent activity chip (Task T17) — shows at a glance when an
              agent is currently editing one of this session's tabs, and for
              how long. Renders nothing when no tab is locked. */}
          <AgentActivityChip sessionId={session.id} />

          {/* One-click Start buttons for detected AI CLI agents. Rendered
              independent of `isInitializing`: that flag tracks the AI
              workspace schema-export pipeline, not the pty — typing
              `claude`/`codex` works the instant the shell exists, so hiding
              the buttons during export would hide them during exactly the
              window this feature exists to fix (D-8). */}
          <AgentStartButtons sessionId={session.id} />

          {/* One-click MCP enable (Task T8). Global server state, not
              session-scoped, so — like the buttons above — it renders
              regardless of `isInitializing` (D-8: that flag tracks the
              schema-export pipeline, not whether the control is usable). */}
          <McpQuickEnableChip />

          {/* Pro upsell badge for Personal mode - clickable */}
          {!hasProAccess && !isInitializing && !initError && (
            <button
              onClick={handleUpgradeClick}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-medium hover:bg-primary/20 transition-colors"
            >
              <Sparkles className="h-3 w-3" />
              PRO
            </button>
          )}

          {/* Expand/collapse indicator */}
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Expanded state - details */}
      {isExpanded && (
        <div className="px-3 py-2 border-t border-border/50 bg-muted/20 text-xs space-y-1.5">
          <div className="font-medium text-muted-foreground mb-2">AI Context Details</div>

          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            {/* Engine */}
            <span className="text-muted-foreground">Engine:</span>
            <span>{getEngineName(engine)}</span>

            {/* Server */}
            <span className="text-muted-foreground">Server:</span>
            <span className="flex items-center gap-1 select-text">
              <Server className="h-3 w-3" />
              {safeConnectionServer(connInfo)}
              {connInfo.port && connInfo.port !== '1433' && connInfo.port !== '5432' && (
                <span className="text-muted-foreground">:{connInfo.port}</span>
              )}
            </span>

            {/* Database */}
            <span className="text-muted-foreground">Database:</span>
            <span className="select-text">{databaseName}</span>

            {/* Workspace path */}
            {session.workspacePath && (
              <>
                <span className="text-muted-foreground">Workspace:</span>
                <span className="truncate max-w-xs select-text" title={session.workspacePath}>
                  {session.workspacePath}
                </span>
              </>
            )}

            {/* Init progress if available */}
            {isInitializing && session.initState?.progress !== undefined && (
              <>
                <span className="text-muted-foreground">Progress:</span>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden max-w-32">
                    <div
                      className="h-full bg-primary transition-[width]"
                      style={{ width: `${session.initState.progress}%` }}
                    />
                  </div>
                  <span className="text-muted-foreground select-text">
                    {session.initState.progress}%
                  </span>
                </div>
              </>
            )}
          </div>

          {/* "Keep this workspace" (Task T16) — opt-in per connection. Pro
              only: a kept workspace is only worth keeping when the workspace
              pipeline (schema export, history, tab sync) is running at all. */}
          {hasProAccess && (
            <PersistentWorkspaceToggleRow session={session} connectionName={serverName} />
          )}

          {/* "Teach ‹agent› about Jam SQL" nudge (Task T15) — shown when a
              detected agent CLI is missing the jam-sql skill. */}
          <AddSkillNudgeRow session={session} />

          {/* Pro upsell banner for Personal mode - clickable */}
          {!hasProAccess && (
            <button
              onClick={handleUpgradeClick}
              className="mt-3 p-2 rounded bg-primary/5 border border-primary/20 hover:bg-primary/10 transition-colors w-full text-left"
            >
              <div className="flex items-center gap-2 text-primary font-medium text-xs mb-1">
                <Sparkles className="h-3.5 w-3.5" />
                Upgrade to Pro for AI Workspace
              </div>
              <p className="text-[11px] text-muted-foreground">
                Get schema sync, query tab sync, MCP tools, and execution history for your AI agents.
              </p>
            </button>
          )}

          {/* Open workspace link - only for Pro users with workspace */}
          {hasProAccess && session.workspacePath && (
            <button
              className="flex items-center gap-1 text-primary hover:underline mt-2"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenWorkspace();
              }}
            >
              <ExternalLink className="h-3 w-3" />
              Open workspace folder
            </button>
          )}
        </div>
      )}
    </div>
  );
}
